import { debounce } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiMutation, useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';

import {
  VendorCreateRequest,
  VendorCreateResponse,
  VendorItem,
  VendorListResponse,
  VendorUpdateRequest,
  VendorUpdateResponse,
} from '../types/productTypes';

interface UseVendorDropdownProps {
  currentVendor: { id: number; vendor: string } | null;
  onVendorChange: (vendor: { id: number; vendor: string }) => void;
}

export const useVendorDropdown = ({
  currentVendor,
  onVendorChange,
}: UseVendorDropdownProps) => {
  // 상태 관리
  const [isOpen, setIsOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [inputValue, setInputValue] = useState(currentVendor?.vendor || '');
  const [editingVendorId, setEditingVendorId] = useState<number | null>(null);
  const [editingVendorName, setEditingVendorName] = useState('');

  // Refs
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // currentVendor 변경 감지
  useEffect(() => {
    if (currentVendor?.vendor !== inputValue) {
      setInputValue(currentVendor?.vendor || '');
    }
  }, [currentVendor?.vendor]);

  // Vendor 목록 조회 쿼리
  const {
    data: vendorListResponse,
    isLoading: isLoadingVendors,
    refetch: refetchVendors,
  } = useApiQuery<VendorListResponse>(
    ['vendors', 'list', searchKeyword],
    () => {
      const endpoint = searchKeyword.trim()
        ? `/api/v1/vendor?keyword=${encodeURIComponent(searchKeyword)}`
        : '/api/v1/vendor';
      return api.get<VendorListResponse>(endpoint);
    },
    {
      enabled: false,
    },
  );

  useEffect(() => {
    if (isOpen) {
      refetchVendors();
    }
  }, [searchKeyword, isOpen, refetchVendors]);

  const vendorList = vendorListResponse?.data?.vendorList || [];

  // Vendor 생성 Mutation
  const createVendorMutation = useApiMutation<
    VendorCreateResponse,
    Error,
    VendorCreateRequest
  >((data) => api.post<VendorCreateResponse>('/api/v1/vendor', data), {
    onSuccess: (response) => {
      if (response.data) {
        const newVendor = {
          id: response.data.id,
          vendor: response.data.vendor,
        };
        // 1. 부모 컴포넌트에 vendor 변경 알림
        onVendorChange(newVendor);
        // 2. input 값 즉시 업데이트
        setInputValue(response.data.vendor);
        // 3. 드롭다운 닫기
        setIsOpen(false);
        // 4. 검색어 초기화
        setSearchKeyword('');
      }
    },
    onError: (error) => {
      alert(`Vendor 생성 중 오류가 발생했습니다: ${error.message}`);
    },
  });

  // Vendor 수정 Mutation
  const updateVendorMutation = useApiMutation<
    VendorUpdateResponse,
    Error,
    { vendorId: number; data: VendorUpdateRequest }
  >(
    ({ vendorId, data }) =>
      api.patch<VendorUpdateResponse>(`/api/v1/vendor/${vendorId}`, data),
    {
      onSuccess: (response) => {
        if (!response.data) return;
        const updatedVendor = {
          id: response.data.id,
          vendor: response.data.vendor,
        };

        // 수정: 항상 수정된 vendor를 선택
        onVendorChange(updatedVendor);
        setInputValue(response.data.vendor);

        setEditingVendorId(null);
        setEditingVendorName('');
        setIsOpen(false);
        refetchVendors();
      },
      onError: (error) => {
        alert(`An error occurred while updating vendor: ${error.message}`);
      },
    },
  );

  // Vendor 삭제 Mutation
  const deleteVendorMutation = useApiMutation<void, Error, number>(
    (vendorId) => api.delete<void>(`/api/v1/vendor/${vendorId}`),
    {
      onSuccess: (_, deletedVendorId) => {
        // 삭제된 vendor가 현재 선택된 vendor와 같다면 초기화
        if (currentVendor?.id === deletedVendorId) {
          onVendorChange({ id: 0, vendor: '' });
          setInputValue('');
        }

        setIsOpen(false);
        refetchVendors();
      },
      onError: (error) => {
        alert(`An error occurred while deleting vendor: ${error.message}`);
      },
    },
  );

  // 디바운스된 검색 함수
  const debouncedSearch = useCallback(
    debounce((keyword: string) => {
      setSearchKeyword(keyword);
      if (keyword.trim()) {
        refetchVendors();
      }
    }, 300),
    [refetchVendors],
  );

  // Input 클릭 핸들러
  const handleInputClick = useCallback(() => {
    setIsOpen(true);
    setSearchKeyword('');
    refetchVendors();
  }, [refetchVendors]);

  // Input 변경 핸들러
  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      if (isOpen) {
        debouncedSearch(value);
      }
    },
    [isOpen, debouncedSearch],
  );

  // Vendor 선택 핸들러
  const handleVendorSelect = useCallback(
    (vendor: VendorItem) => {
      const selectedVendor = {
        id: vendor.id,
        vendor: vendor.vendor,
      };
      onVendorChange(selectedVendor);
      setInputValue(vendor.vendor);
      setIsOpen(false);
      setSearchKeyword('');
    },
    [onVendorChange],
  );

  // Vendor 생성 핸들러
  const handleVendorCreate = useCallback(() => {
    if (searchKeyword.trim()) {
      createVendorMutation.mutate({ vendor: searchKeyword.trim() });
    }
  }, [searchKeyword, createVendorMutation]);

  // Vendor 수정 시작 핸들러
  const handleVendorEditStart = useCallback((vendor: VendorItem) => {
    setEditingVendorId(vendor.id);
    setEditingVendorName(vendor.vendor);
  }, []);

  // Vendor 수정 완료 핸들러
  const handleVendorEditComplete = useCallback(() => {
    if (editingVendorId && editingVendorName.trim()) {
      updateVendorMutation.mutate({
        vendorId: editingVendorId,
        data: { vendor: editingVendorName.trim() },
      });
    }
    setInputValue(editingVendorName);
    setEditingVendorId(null);
    setEditingVendorName('');
    setIsOpen(false);
  }, [editingVendorId, editingVendorName, updateVendorMutation]);

  // Vendor 수정 취소 핸들러
  const handleVendorEditCancel = useCallback(() => {
    setEditingVendorId(null);
    setEditingVendorName('');
  }, []);

  // Vendor 삭제 핸들러
  const handleVendorDelete = useCallback(
    (vendorId: number) => {
      if (window.confirm('Are you sure to delete vendor?')) {
        deleteVendorMutation.mutate(vendorId);
      }
    },
    [deleteVendorMutation],
  );

  // 드롭다운 닫기 핸들러
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setSearchKeyword('');
    setEditingVendorId(null);
    setEditingVendorName('');
  }, []);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  // 검색 결과에 정확히 일치하는 vendor가 있는지 확인
  const hasExactMatch = vendorList.some(
    (vendor) => vendor.vendor.toLowerCase() === searchKeyword.toLowerCase(),
  );

  // 생성 버튼을 보여줄지 결정
  const showCreateButton =
    searchKeyword.trim() && !hasExactMatch && vendorList.length === 0;

  const isCreating = createVendorMutation.status === 'pending';
  const isUpdating = updateVendorMutation.status === 'pending';
  const isDeleting = deleteVendorMutation.status === 'pending';

  return {
    // 상태
    isOpen,
    inputValue,
    vendorList,
    editingVendorId,
    editingVendorName,
    showCreateButton,
    searchKeyword,

    // 로딩 상태
    isLoadingVendors,
    isCreating,
    isUpdating,
    isDeleting,

    // 핸들러
    handleInputClick,
    handleInputChange,
    handleVendorSelect,
    handleVendorCreate,
    handleVendorEditStart,
    handleVendorEditComplete,
    handleVendorEditCancel,
    handleVendorDelete,
    handleClose,
    setEditingVendorName,

    // Refs
    dropdownRef,
    inputRef,
  };
};
